// src/pages/SellerProducts.js
import React, { useEffect, useState, useRef } from 'react';
import API from '../api';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { SellerSpinner } from '../components/skeleton';
import '../styles/SellerCommon.css';
import '../styles/SellerProducts.css';

function SellerProducts() {
  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(50); // Pagina di default con 50 prodotti
  const [totalProducts, setTotalProducts] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const observer = useRef();
  const sentinelRef = useRef();

  useEffect(() => {
    setProducts([]);
    setPage(1);
    setHasMore(true);
  }, [size]);

  useEffect(() => {
    // Recupera i prodotti del venditore con paginazione
    const fetchSellerProducts = async () => {
      setProductsLoading(true);
      setMessage('');
      try {
        const res = await API.get('/auth/seller-products', {
          params: { page, size },
        });
        if (res.data.success) {
          setProducts(prev => page === 1 ? res.data.products : [...prev, ...res.data.products]);
          setTotalProducts(res.data.total); // Totale prodotti
          setTotalPages(res.data.totalPages); // Numero totale di pagine
          setHasMore(page < res.data.totalPages);
        } else {
          setMessage('Errore nel recupero dei prodotti.');
        }
      } catch (error) {
        console.error('Errore nel recupero dei prodotti:', error);
        setMessage('Errore nel recupero dei prodotti.');
      } finally {
        setProductsLoading(false);
      }
    };

    fetchSellerProducts();
  }, [page, size]);

  useEffect(() => {
    if (productsLoading) return;
    if (!hasMore) return;
    if (observer.current) observer.current.disconnect();
    observer.current = new window.IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore && !productsLoading) {
        setPage(prev => prev + 1);
      }
    });
    if (sentinelRef.current) observer.current.observe(sentinelRef.current);
    return () => observer.current && observer.current.disconnect();
  }, [productsLoading, hasMore, products]);

  const handlePageChange = (newPage) => {
    if (newPage > 0 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  if (productsLoading && products.length === 0) {
    return <SellerSpinner text="Caricamento prodotti..." />;
  }

  return (
    <>
      <div className="seller-page-header">
        <h1>Prodotti</h1>
        <p>Gestisci e visualizza tutti i tuoi prodotti integrati</p>
      </div>

      {message && (
        <div className={`seller-message ${message.includes('Errore') ? 'error' : 'success'}`}>
          {message}
        </div>
      )}

      {/* Conteggio totale prodotti */}
      <div className="seller-products-stats">
        <div className="seller-products-stat-card">
          <div className="seller-products-stat-label">Totale Prodotti</div>
          <div className="seller-products-stat-value">{totalProducts}</div>
        </div>
        <div className="seller-products-stat-card">
          <div className="seller-products-stat-label">Pagina Corrente</div>
          <div className="seller-products-stat-value">{page} di {totalPages}</div>
        </div>
      </div>

      {/* Prodotti del Venditore */}
      <div className="seller-card">
        <div className="seller-card-header">
          <h3>Tutti i prodotti</h3>
          <div className="seller-products-controls">
            <select 
              value={size} 
              onChange={(e) => setSize(Number(e.target.value))}
              className="seller-form-control"
            >
              <option value={20}>20 per pagina</option>
              <option value={50}>50 per pagina</option>
              <option value={100}>100 per pagina</option>
            </select>
          </div>
        </div>
        <div className="seller-card-body">
          {productsLoading && products.length === 0 ? (
            <SellerSpinner text="Caricamento prodotti..." />
          ) : products.length > 0 ? (
            <div className="seller-products-table-container">
              <table className="seller-products-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Immagine</th>
                    <th>Titolo</th>
                    <th>Prezzo</th>
                    <th>Varianti</th>
                    <th>Quantità</th>
                    <th>Dettagli</th>
                  </tr>
                </thead>
                <tbody>
                  {products.map((product) => {
                    // Calcolo sconto
                    const hasDiscount = product.compare_at_price_min && product.compare_at_price_min > product.price_min;
                    const discountPerc = hasDiscount ? Math.round(100 * (product.compare_at_price_min - product.price_min) / product.compare_at_price_min) : 0;
                    const showRange = product.price_min !== product.price_max;
                    return (
                      <tr key={product.id}>
                        <td className="seller-products-table-cell">{product.id}</td>
                        <td className="seller-products-table-cell">
                          <img
                            src={product.main_image_url}
                            alt={product.title}
                            className="seller-products-product-img"
                          />
                        </td>
                        <td className="seller-products-table-cell">{product.title}</td>
                        <td className="seller-products-table-cell">
                          {hasDiscount ? (
                            <>
                              <span className="seller-products-compare-price">
                                €{parseFloat(product.compare_at_price_min).toFixed(2)}
                              </span>
                              <span className="seller-products-price">
                                €{parseFloat(product.price_min).toFixed(2)}
                              </span>
                              {showRange && (
                                <span className="seller-products-price-range">- €{parseFloat(product.price_max).toFixed(2)}</span>
                              )}
                              <span className="seller-products-discount-badge">-{discountPerc}%</span>
                            </>
                          ) : (
                            <>
                              <span className="seller-products-price">
                                €{parseFloat(product.price_min).toFixed(2)}
                              </span>
                              {showRange && (
                                <span className="seller-products-price-range">- €{parseFloat(product.price_max).toFixed(2)}</span>
                              )}
                            </>
                          )}
                        </td>
                        <td className="seller-products-table-cell">{Array.isArray(product.variants) ? product.variants.length : '-'}</td>
                        <td className="seller-products-table-cell">{product.stock}</td>
                        <td className="seller-products-table-cell">
                          <Link
                            className="seller-btn seller-btn-primary seller-btn-sm"
                            to={`/products/${product.id}`}
                          >
                            Vedi
                          </Link>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="seller-products-empty">
              <p>Nessun prodotto disponibile.</p>
            </div>
          )}
          {/* Sentinella per infinite scroll */}
          <div ref={sentinelRef} style={{ height: 1 }} />
          {productsLoading && products.length > 0 && (
            <SellerSpinner text="Caricamento altri prodotti..." size="small" />
          )}
        </div>
      </div>
    </>
  );
}

export default SellerProducts;
